/**
 * NOTE: This component duplicates the component at /components/brands/ProductCatalogSection/ProductCard.tsx
 *
 * This version uses a generic Product type with a subset of attributes that
 * are returned from product API endpoints. It has also been extended to include
 * passing arbitrary anchor props to the root anchor element.
 *
 * This version should be used when these two components are unified.
 */

import React, { AnchorHTMLAttributes } from "react";
import classNames from "classnames";
import { useSelector } from "react-redux";

import { getDomainCountryCode } from "redux/selectors/config";
import currencyFormatter from "utils/currencyFormatter";

import StarRating from "components/botanic/StarRating";
import Image from "components/Image";

type Product = {
  brand: {
    slug: string;
  };
  slug: string;
  thumbnailImageUrl: string;
  imageUrl: string;
  name: string;
  subcategoryName: string;
  categoryName: string;
  avgApprovedPublicReviewRating: number;
  countApprovedPublicReviews: number;
  bestOfferVariant?: { price: number };
};

type Props = {
  product: Product;
  className?: string;
} & AnchorHTMLAttributes<HTMLAnchorElement>;

export const ProductCard = ({ product, className, ...props }: Props) => {
  const countryCode = useSelector(getDomainCountryCode);

  /**
   * Previously, this link could have pointed to a specific offer on the product
   * detail page, which only showed the "best" offer, with other offers only
   * visible behind a second click. The new product page shows a carousel of
   * offers (at the time of writing, 2021/10/20), which should include the
   * "best" offer among those initially visible. The previous conditional exists
   * below for reference, as it may be useful in the future if the design of the
   * product page changes to only show the "best" offer.
   *
   * // Hooks moved from top of component for context:
   * import { useRouter } from 'next/router'
   * import { hasMobileParam } from '../../lib/paramCheck'
   * const router = useRouter()
   * const isNative = hasMobileParam(router)
   *
   * let href = `/products/details/${product.slug}`
   * if (!isNative && product?.bestOfferVariant?.dispensary) {
   *   const {
   *     dispensary: { slug },
   *     displayQuantity,
   *     price,
   *   } = product.bestOfferVariant
   *   href = `${href}?dispensary=${slug}&price=${price}&quantity=${displayQuantity}`
   * }
   */
  const href = `/brands/${product.brand.slug}/products/${product.slug}`;

  return (
    <a
      className={classNames(
        "elevation-low rounded overflow-hidden p-lg pt-xl flex flex-col justify-between",
        className
      )}
      href={href}
      {...props}
    >
      <div>
        <div className="p-0 mx-auto" style={{ width: "112px" }}>
          {(product.thumbnailImageUrl || product.imageUrl) && (
            <Image
              src={
                product.thumbnailImageUrl
                  ? product.thumbnailImageUrl
                  : product.imageUrl
              }
              sizes={[112]}
              className="mb-md"
              alt={`Product image for ${product.name}`}
              ratio="square"
            />
          )}
        </div>
        <div className="text-secondary text-xs leading-none mb-xs">
          {product.subcategoryName
            ? product.subcategoryName
            : product.categoryName}
        </div>
        <h3 className="font-bold text-sm truncate-lines truncate-2-lines">
          {product.name}
        </h3>
        {!!product.avgApprovedPublicReviewRating && (
          <StarRating
            rating={product.avgApprovedPublicReviewRating}
            ratingCount={product.countApprovedPublicReviews}
            showRating={true}
            starWidth={14}
            className="my-xs"
          />
        )}
      </div>
      {!!product.bestOfferVariant && (
        <div className="mt-xs text-xs font-bold text-grey">
          Starting at
          <div className="text-sm font-bold text-default">
            {currencyFormatter(product.bestOfferVariant.price, countryCode)}
          </div>
        </div>
      )}
    </a>
  );
};
